import type { SkillLevels } from 'bundles/enterprise-admin-skills/utils/skillTargets';
import type { CREATE_LEARNER_STEPS, IMPORT_LEARNERS_STEPS } from 'bundles/enterprise-admin-users/constants';

import type {
  ProgramsAndRostersByOrganizationQuery_EnterpriseProgramsV1Resource_byThirdPartyOrganizationId_elements as Program,
  ProgramsAndRostersByOrganizationQuery_EnterpriseProgramsV1Resource_byThirdPartyOrganizationId_elements_userRosters_elements as UserRoster,
} from './__generated__/ProgramsAndRostersByOrganizationQuery';

export enum ActivityStatus {
  active = 'ACTIVE',
  inactive = 'INACTIVE',
  completed = 'COMPLETED',
}

export type User = {
  email: string;
  fullName: string;
  userIdentifier: string;
  userId: number;
  externalId?: string;
  programNames?: string[];
  productNames?: string[];
  numEnrollments?: number;
  numCompletions?: number;
  invitedAt?: number;
  joinedAt?: number;
  latestActivityAt?: number;
  lastActivityAt?: number;
  deletedAt?: number;
  isSoftDeleted?: boolean;
  status?: ActivityStatus | null;
  // Skills
  skillScores?: number[];
  skillDetails?: {
    proficiency: SkillLevels;
    score: number;
    skillId: string;
    skillName: string;
  }[];
  skillIds?: string[];
  targetSkillProfilesProgress?: number[];
  employabilityRanks?: number[];
  // Gradebook
  grade?: number;
  riskFlagsCount?: number;
  enrolledAt?: number;
  completedAt?: number;
  progressPercentage?: number;
  isLearnerRemoved?: boolean;
  courseIds?: string[];
  // Gradebook Plagiarism Report
  plagiarismIncidents?: number;
  appealsGranted?: number;
  // ELP
  managerName?: string;
  managerEmail?: string;
  locationCountry?: string;
  locationRegion?: string;
  locationCity?: string;
  businessUnit?: string;
  businessUnit2?: string;
};

export enum AttributeSelections {
  learner = 'NAME',
  email = 'EMAIL',
  userId = 'USER_ID',
  joinedAt = 'JOINED_AT',
  invitedAt = 'INVITED_AT',
  programNames = 'PROGRAM_IDS',
  contractNames = 'CONTRACT',
  productNames = 'PRODUCT_ITEM_IDS',
  numEnrollments = 'NUM_ENROLLMENTS',
  numEnrollmentsIncludingDowngraded = 'NUM_ENROLLMENTS',
  numCompletions = 'NUM_COMPLETIONS',
  numCompletionsIncludingDowngraded = 'NUM_COMPLETIONS',
  externalId = 'EXTERNAL_ID',
  latestActivityAt = 'LATEST_ACTIVITY_AT',
  lastActivityAt = 'LAST_ACTIVITY_AT',
  deletedAt = 'DELETED_AT',
  isSoftDeleted = 'IS_SOFT_DELETED',
  status = 'STATUS',
  // Skills
  skillIds = 'SKILL_IDS',
  skillMetTargets = 'SKILL_MET_TARGETS',
  skillProficiencies = 'SKILL_PROFICIENCIES',
  skillScores = 'SKILL_SCORES',
  targetSkillProfileProgress = 'TARGET_SKILL_PROFILE_PROGRESS',
  employabilityRanks = 'EMPLOYABILITY_RANKS',
  // Gradebook
  grade = 'GRADE',
  enrolledAt = 'ENROLLED_AT',
  completedAt = 'COMPLETED_AT',
  riskFlagsCount = 'RISK_FLAGS_COUNT',
  progressPercentage = 'PROGRESS_PERCENTAGE',
  courseName = 'COURSE_NAME',
  isLearnerRemoved = 'is_membership_deleted',
  plagiarismCount = 'PLAGIARISM_COUNT',
  learnerEffort = 'learningHours',
  courseLevelRiskFlagsCount = 'courseLevelRiskFlagsCount',
  // Program Plagiarism Report
  plagiarismIncidents = 'plagiarismconfirmedcount',
  appealsGranted = 'appealsGrantedCount',
  // Enhanced Learner Profile,
  businessUnits = 'BUSINESS_UNITS',
  jobTitle = 'JOB_TITLE',
  location = 'LOCATION',
  manager = 'MANAGER',
  jobType = 'JOB_TYPE',
}

export type Users = User[];

export type ImportLearnersSteps = (typeof IMPORT_LEARNERS_STEPS)[keyof typeof IMPORT_LEARNERS_STEPS];
export type CreateLearnerSteps = (typeof CREATE_LEARNER_STEPS)[keyof typeof CREATE_LEARNER_STEPS];

export type { UserRoster, Program };

export type S3Location = {
  key: string;
  bucket: string;
};

export type ImportProcessingTypes = 'synchronousImport' | 'asynchronousImport';

export type ImportResponse = {
  data: {
    reportPath: string;
  };
};

export type LearnerDetails = {
  name: string;
  email: string;
  externalId: string;
};
